interface UnsplashSearchResponse {
  total: number;
  total_pages: number;
  results: UnsplashImage[];
}

interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  alt_description: string;
  user: {
    name: string;
    username: string;
  };
  width: number;
  height: number;
  links: {
    download_location: string;
  };
}

class UnsplashServiceClass {
  private readonly accessKey = import.meta.env.VITE_UNSPLASH_ACCESS_KEY;
  private readonly baseUrl = 'https://api.unsplash.com';

  async searchPhotos(query: string, page: number = 1, perPage: number = 20): Promise<UnsplashSearchResponse> {
    if (!this.accessKey) {
      throw new Error('Unsplash API key not configured. Please set VITE_UNSPLASH_ACCESS_KEY in your .env file.');
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/search/photos?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}&orientation=landscape`,
        {
          headers: {
            'Authorization': `Client-ID ${this.accessKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching Unsplash photos:', error);
      throw error;
    }
  }

  async triggerDownload(photoId: string): Promise<void> {
    try {
      // First get the photo details to get the download location
      const photoResponse = await fetch(
        `${this.baseUrl}/photos/${photoId}`,
        {
          headers: {
            'Authorization': `Client-ID ${this.accessKey}`,
          },
        }
      );

      if (!photoResponse.ok) {
        throw new Error(`Failed to get photo details: ${photoResponse.status}`);
      }

      const photo = await photoResponse.json();
      
      // Trigger the download tracking
      await fetch(photo.links.download_location, {
        headers: {
          'Authorization': `Client-ID ${this.accessKey}`,
        },
      });
    } catch (error) {
      console.error('Error triggering Unsplash download:', error);
      // Don't throw here as this is just for tracking
    }
  }

  async getRandomPhotos(count: number = 10, query?: string): Promise<UnsplashImage[]> {
    try {
      const url = query 
        ? `${this.baseUrl}/photos/random?count=${count}&query=${encodeURIComponent(query)}&orientation=landscape`
        : `${this.baseUrl}/photos/random?count=${count}&orientation=landscape`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Client-ID ${this.accessKey}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [data];
    } catch (error) {
      console.error('Error getting random Unsplash photos:', error);
      throw error;
    }
  }

  async getFeaturedPhotos(page: number = 1, perPage: number = 20): Promise<UnsplashImage[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/photos?page=${page}&per_page=${perPage}&order_by=popular`,
        {
          headers: {
            'Authorization': `Client-ID ${this.accessKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting featured Unsplash photos:', error);
      throw error;
    }
  }
}

export const UnsplashService = new UnsplashServiceClass();